import type { KeyboardEvent, ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronRight } from 'lucide-react';
import { CircularLoading, CNButton } from '@zawarski/eti-ui';
import type { SortOrder } from '@components/rep';
import { cn } from '@utils/index';

export interface DataTableColumn<T> {
  field: string;
  headerName: string;
  renderCell?: (row: T) => ReactNode;
  /** false: a plain heading with no sort button, for a column of row actions. */
  sortable?: boolean;
}

interface DataTableProps<T extends Record<string, unknown>> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  loading?: boolean;
  isMobile?: boolean;
  order: SortOrder;
  orderBy: string;
  onRequestSort: (field: string) => void;
  /** 1-based page; the footer shows once there is a page. */
  page?: number;
  pageCount?: number;
  onPageChange?: (page: number) => void;
  onRowClick?: (row: T) => void;
  /** Phone layout: one card per row. */
  renderMobileCard?: (row: T) => ReactNode;
  /** Shown in place of the rows when there are none; nothing is shown when omitted. */
  emptyMessage?: string;
}

/**
 * The Figma pager buttons' corners, text and border; eti-ui's outline button is slate, 14px and
 * 6px-cornered, and its stylesheet loads after ours, so classes cannot override it.
 */
const PAGER_BUTTON_STYLE = {
  borderRadius: 8,
  fontSize: 12,
  borderColor: '#E5E5E5',
  color: '#0A0A0A',
};

/** Every row's divider; the bordered wrapper closes the last one. */
const ROW_CLASS =
  'rep-table-row rep:border-b rep:border-[#E5E5E5] rep:last:border-b-0 rep:outline-none';
const OPENABLE_ROW_CLASS =
  'rep:cursor-pointer rep:hover:bg-[#F5F5F5] rep:focus-visible:bg-[#F0F3FF]';

/**
 * The paged table's footer (Figma 2782:4253): "Page X of Y" with Previous / Next. Renders nothing
 * until there is a page. Shared with tables that draw their own rows (the File Library).
 */
export const TablePager = ({
  page,
  pageCount,
  onPageChange,
}: {
  /** 1-based. */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}) =>
  pageCount > 0 ? (
    <div className='palmetto-print-hide rep:flex rep:items-center rep:justify-between rep:gap-4'>
      <div className='rep:text-sm rep:text-[#737373]'>{`Page ${page} of ${pageCount}`}</div>
      <div className='rep:flex rep:gap-2'>
        <CNButton
          type='button'
          variant='outline'
          size='sm'
          style={PAGER_BUTTON_STYLE}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}>
          Previous
        </CNButton>
        <CNButton
          type='button'
          variant='outline'
          size='sm'
          style={PAGER_BUTTON_STYLE}
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}>
          Next
        </CNButton>
      </div>
    </div>
  ) : null;

/**
 * The Palmetto 3.0 paged table (Figma Virtual EOC 2782:4056, footer 2782:4253): a bordered,
 * rounded table with sortable column heads and 52px rows that open on click or Enter, a card per
 * row on phones, and a "Page X of Y" footer with Previous / Next. While `loading`, only a spinner.
 */
const DataTable = <T extends Record<string, unknown>>({
  columns,
  rows,
  rowKey,
  loading,
  isMobile,
  order,
  orderBy,
  onRequestSort,
  page = 1,
  pageCount = 0,
  onPageChange,
  onRowClick,
  renderMobileCard,
  emptyMessage,
}: DataTableProps<T>) => {
  if (loading) {
    return (
      <div className='rep:flex rep:h-[60vh] rep:w-full rep:items-center rep:justify-center'>
        <CircularLoading size='md' />
      </div>
    );
  }

  const showEmptyMessage = rows.length === 0 && !!emptyMessage;
  const openProps = (row: T) =>
    onRowClick
      ? {
          tabIndex: 0,
          onClick: () => onRowClick(row),
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Enter') onRowClick(row);
          },
        }
      : {};

  return (
    <div className='rep-table-grid rep:flex rep:w-full rep:flex-col rep:gap-4'>
      <div className='rep:w-full rep:overflow-x-auto rep:rounded-lg rep:border rep:border-[#E5E5E5] rep:bg-white'>
        {isMobile && renderMobileCard ? (
          <>
            {showEmptyMessage ? (
              <p className='rep:px-3 rep:py-4 rep:text-center rep:text-sm rep:text-[#737373]'>
                {emptyMessage}
              </p>
            ) : null}
            {rows.map((row) => (
              <div
                key={rowKey(row)}
                className={cn(
                  ROW_CLASS,
                  'rep:flex rep:flex-col rep:gap-1 rep:p-3',
                  onRowClick && OPENABLE_ROW_CLASS,
                )}
                {...openProps(row)}>
                {renderMobileCard(row)}
              </div>
            ))}
          </>
        ) : (
          <table className='rep:w-full rep:border-collapse rep:text-left rep:text-sm rep:text-[#0A0A0A]'>
            <thead>
              <tr className='rep:border-b rep:border-[#E5E5E5]'>
                {columns.map((column) => {
                  const sortable = column.sortable !== false;
                  const sorted = sortable && orderBy === column.field;
                  return (
                    <th
                      key={column.field}
                      scope='col'
                      aria-sort={
                        sorted ? (order === 'asc' ? 'ascending' : 'descending') : undefined
                      }
                      className='rep:h-10 rep:px-2 rep:font-medium rep:whitespace-nowrap'>
                      {sortable ? (
                        <button
                          type='button'
                          onClick={() => onRequestSort(column.field)}
                          className='rep:inline-flex rep:cursor-pointer rep:items-center rep:gap-1 rep:rounded rep:outline-none rep:focus-visible:ring-2 rep:focus-visible:ring-[#0949EE]'>
                          {column.headerName}
                          {sorted ? (
                            order === 'asc' ? (
                              <ArrowUp className='rep:size-3.5 rep:text-[#737373]' />
                            ) : (
                              <ArrowDown className='rep:size-3.5 rep:text-[#737373]' />
                            )
                          ) : null}
                        </button>
                      ) : (
                        column.headerName
                      )}
                    </th>
                  );
                })}
                {onRowClick ? <td className='rep:w-12' /> : null}
              </tr>
            </thead>
            <tbody>
              {showEmptyMessage ? (
                // Left-aligned: on a table wider than the card, a centered message lands off screen.
                <tr>
                  <td
                    colSpan={columns.length + (onRowClick ? 1 : 0)}
                    className='rep:px-2 rep:py-4 rep:text-sm rep:text-[#737373]'>
                    {emptyMessage}
                  </td>
                </tr>
              ) : null}
              {rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={cn(ROW_CLASS, 'rep:h-13', onRowClick && OPENABLE_ROW_CLASS)}
                  {...openProps(row)}>
                  {columns.map((column, index) => {
                    const content = column.renderCell
                      ? column.renderCell(row)
                      : ((row[column.field] as ReactNode) ?? '');
                    const cell = <div className='rep:max-w-60 rep:truncate'>{content}</div>;
                    return index === 0 ? (
                      <th key={column.field} scope='row' className='rep:p-2 rep:font-normal'>
                        {cell}
                      </th>
                    ) : (
                      <td key={column.field} className='rep:p-2'>
                        {cell}
                      </td>
                    );
                  })}
                  {/* The "opens" arrow, only on rows that open. */}
                  {onRowClick ? (
                    <td className='rep:w-12 rep:p-2 rep:text-[#737373]'>
                      <ChevronRight className='rep:size-4' />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {onPageChange ? (
        <TablePager page={page} pageCount={pageCount} onPageChange={onPageChange} />
      ) : null}
    </div>
  );
};

export default DataTable;
